import { IAsyncData, IVehicle, IVehicleDetails, IDigitalChecklistAppointment, ICustomer, ISalesAdvisor, IDigitalChecklistRequest, IDigitalChecklist } from '@signal/asp-data-commons';
import produce from 'immer';
import * as fromActions from '../delivery-checklist/delivery-checklist.actions';
import { editDataPopulatedInState } from './delivery-checklist.adapter';

export const USER = {
  TECHNICIAN: 'TECHNICIAN',
  CUSTOMER: 'CUSTOMER'
};

export interface IChecklistSummaryState extends IAsyncData<IDigitalChecklistAppointment[]> {
  allChecklistCount: number;
  totalCount:number;
}

export interface IArchivedChecklistSummaryState extends IAsyncData<IDigitalChecklistAppointment[]> {
  archivedChecklistsCount: number;
  totalCount:number;
  year:number;
  month:number;
}

export interface IDigitalChecklistForm {
  vehicle: IVehicleDetails;
  salesConsultant: string;
  dateOfAcquired: string;
  dateOfDeletion:string;
  customer: ICustomer;
  customerSignId?: string;
  deliveryConsultant: ISalesAdvisor;
  checklistRequest?: IDigitalChecklistRequest[];
  scheduledTime?: string;
  descriptionOfProductsOrServices?: string;
}

export interface IEditDetails {
  isEditMode?: boolean;
  digiChekId: string;
  dealerCode?: string;
  isSubmitted?: boolean;
  checklistData?: IDigitalChecklistAppointment;
}

export interface DeliveryChecklistState {
  editDetails: IEditDetails,
  checklistSummary: IChecklistSummaryState,
  archivedChecklists: IArchivedChecklistSummaryState,
  digitalChecklist: IDigitalChecklistForm,
  checklistRequest: IAsyncData<IDigitalChecklist[]>
}

export function deliveryChecklistReducer(
  state: DeliveryChecklistState = initialState,
  action: fromActions.DeliveryChecklistActions): DeliveryChecklistState {

  switch (action.type) {
    case fromActions.DeliveryChecklistActionTypes.LOAD_CHECKLIST_SUMMARY: {
      return produce(state, draftState => {
        draftState.checklistSummary = {
          data: state.checklistSummary.data,
          loading: true,
          loaded: false,
          error: '',
          allChecklistCount: state.checklistSummary.allChecklistCount,
          totalCount: state.checklistSummary.totalCount
        }
      })
    }

    case fromActions.DeliveryChecklistActionTypes.LOAD_CHECKLIST_SUMMARY_SUCCESS: {
      return produce(state, draftState => {
        const payload = action instanceof fromActions.LoadChecklistSummarySuccess ? action.payload : undefined;
        draftState.checklistSummary = {
          data: (state.checklistSummary.data?.length && payload.appendData) ? [...state.checklistSummary.data,...payload.checklist] : payload.checklist,
          loading: false,
          loaded: true,
          error: '',
          allChecklistCount: ((state.checklistSummary.data?.length && payload.appendData) ? state.checklistSummary.data.length+payload.checklist?.length : payload.checklist?.length) || 0,
          totalCount:payload.totalCount || 0
        }
      })
    }

    case fromActions.DeliveryChecklistActionTypes.LOAD_CHECKLIST_SUMMARY_FAILURE: {
      return produce(state, draftState => {
        draftState.checklistSummary = {
          data: undefined,
          loading: false,
          loaded: false,
          error: action instanceof fromActions.LoadChecklistSummaryFailure ? action.payload.error : '',
          allChecklistCount: null,
          totalCount: null,
        }
      })
    }

    case fromActions.DeliveryChecklistActionTypes.CLEAN_CHECKLIST_SUMMARY_STATE: {
      return produce(state, draftState => {
        draftState.checklistSummary = {
          data: undefined,
          loading: false,
          loaded: false,
          error: '',
          allChecklistCount: null,
          totalCount: null
        }
      })
    }

    case fromActions.DeliveryChecklistActionTypes.LOAD_ARCHIVED_CHECKLISTS: {
      return produce(state, draftState => {
        draftState.archivedChecklists = {
          data: state.archivedChecklists.data,
          loading: true,
          loaded: false,
          error: '',
          archivedChecklistsCount:state.archivedChecklists.archivedChecklistsCount,
          totalCount:state.archivedChecklists.totalCount,
          year:state.archivedChecklists.year,
          month:state.archivedChecklists.month
        }
      })
    }
    
    case fromActions.DeliveryChecklistActionTypes.LOAD_ARCHIVED_CHECKLISTS_SUCCESS: {
      return produce(state, draftState => {
        const payload = action instanceof fromActions.LoadArchivedChecklistsSuccess ? action.payload : undefined;
        draftState.archivedChecklists = {
          data: (state.archivedChecklists.year!==payload.year || state.archivedChecklists.month!==payload.month) ? payload.checklistDetails : [...state.archivedChecklists.data,...payload.checklistDetails],
          loading: false,
          loaded: true,
          error: '',
          archivedChecklistsCount:((state.archivedChecklists.year!==payload.year || state.archivedChecklists.month!==payload.month) ? payload.checklistDetails?.length : state.archivedChecklists.data.length + payload.checklistDetails.length) || 0,
          totalCount: payload.totalCount || 0,
          year:payload.year,
          month:payload.month
        }
      })
    }

    case fromActions.DeliveryChecklistActionTypes.LOAD_ARCHIVED_CHECKLISTS_FAILURE: {
      return produce(state, draftState => {
        draftState.archivedChecklists = {
          data: undefined,
          loading: false,
          loaded: false,
          error: action instanceof fromActions.LoadArchivedChecklistsFailure ? action.payload.error : '',
          archivedChecklistsCount:null,
          totalCount:null,
          year:null,
          month:null
        }
      })
    }

    case fromActions.DeliveryChecklistActionTypes.CLEAN_ARCHIVED_SUMMARY_STATE: {
      return produce(state, draftState => {
        draftState.archivedChecklists = {
          data: undefined,
          loading: false,
          loaded: false,
          error: '',
          archivedChecklistsCount:null,
          totalCount:null,
          year:null,
          month:null
        }
      })
    }

    case fromActions.DeliveryChecklistActionTypes.UPDATE_FORM_DATA: {
      const payload = action instanceof fromActions.UpdateFormData ? action.payload : undefined;
      return produce(state, draftState => {
        draftState.digitalChecklist = {
          ...payload.value,
          checklistRequest: state.digitalChecklist.checklistRequest,
          customerSignId: state.digitalChecklist.customerSignId,
          deliveryConsultant: state.digitalChecklist.deliveryConsultant,
          descriptionOfProductsOrServices: state.digitalChecklist.descriptionOfProductsOrServices ? state.digitalChecklist.descriptionOfProductsOrServices : ""
        }
      })
    }

    case fromActions.DeliveryChecklistActionTypes.LOAD_CHECKLIST: {
      return produce(state, draftState => {
        draftState.checklistRequest = {
          data: [],
          loading: true,
          loaded: false,
          error: ''
        };
      });
    }

    case fromActions.DeliveryChecklistActionTypes.LOAD_CHECKLIST_SUCCESS: {
      return produce(state, draftState => {
        const payload = action instanceof fromActions.LoadChecklistSuccess ? action.payload : undefined;
        draftState.checklistRequest = {
          data: payload.checklistDetails,
          loading: false,
          loaded: true,
          error: ''
        };
      });
    }

    case fromActions.DeliveryChecklistActionTypes.LOAD_CHECKLIST_FAILURE: {
      return produce(state, draftState => {
        const payload = action instanceof fromActions.LoadChecklistFailure ? action.payload : undefined;
        draftState.checklistRequest = {
          data: [],
          loading: false,
          loaded: false,
          error: payload.error
        };
      });
    }

    case fromActions.DeliveryChecklistActionTypes.CREATE_CHECKLIST: {
      return produce(state, draftState => {
        const payload = action instanceof fromActions.CreateChecklist ? action.payload : undefined;

        draftState.digitalChecklist.checklistRequest = payload.data;
      });
    }

    case fromActions.DeliveryChecklistActionTypes.UPDATE_SIGNATURE_FORM_DATA: {
      const payload = action instanceof fromActions.UpdateSignatureFormData ? action.payload.value : undefined;
      return produce(state, draftState => {
        if (payload.type === USER.CUSTOMER) {
          if (payload.id) {
            draftState.digitalChecklist.customerSignId = payload.id;
          }
          if (state.digitalChecklist.customer.firstName !== payload.firstName ||
            state.digitalChecklist.customer.lastName !== payload.lastName) {
            draftState.digitalChecklist.customer = {
              ...state.digitalChecklist.customer,
              firstName: payload.firstName,
              lastName: payload.lastName
            }
          }
        }
        else if (payload.type === USER.TECHNICIAN) {
          draftState.digitalChecklist.deliveryConsultant = {
            firstName: payload.firstName,
            lastName: payload.lastName,
            salesAdvisorSignId: payload.id ? payload.id : '',
            ownerId: payload.ownerId,
          }
        }
      }
      )
    }

    case fromActions.DeliveryChecklistActionTypes.UPDATE_DESCRIPTION: {
      return produce(state, draftState => {
        const payload = action instanceof fromActions.UpdateDescription ? action.payload : undefined;
        draftState.digitalChecklist.descriptionOfProductsOrServices = payload.description;
      });
    }

    case fromActions.DeliveryChecklistActionTypes.CREATE_DIGICHECK_REQUEST_SUCCESS: {
      return produce(state, draftState => {
        const payload = action instanceof fromActions.CreateDigiChekReqSuccess ? action.payload : undefined;
        draftState.editDetails = {
          digiChekId: payload.reqModel.digichkId,
          isSubmitted: true,
          checklistData: payload.reqModel
        };
      });
    }

    case fromActions.DeliveryChecklistActionTypes.CREATE_DIGICHECK_REQUEST_FAILURE:
    case fromActions.DeliveryChecklistActionTypes.EDIT_DIGICHECK_REQUEST_FAILURE: {
      return produce(state, draftState => {
        draftState.editDetails = {
          digiChekId: '',
          isSubmitted: false
        };
      });
    }

    case fromActions.DeliveryChecklistActionTypes.RESET_DIGICHECK_STATE: {
      return initialState;
    }

    case fromActions.DeliveryChecklistActionTypes.GET_EDIT_DATA: {
      return produce(state, draftState => {
        const payload = action instanceof fromActions.GetEditData ? action.payload : undefined;
        draftState.editDetails = {
          digiChekId: payload.digiChekId,
          isSubmitted: false
        };
      });
    }

    case fromActions.DeliveryChecklistActionTypes.GET_EDIT_DATA_SUCCESS: {
      return produce(state, draftState => {
        const payload = action instanceof fromActions.GetEditDataSuccess ? action.payload : undefined;
        draftState.digitalChecklist = editDataPopulatedInState(payload.checklistModel);
        draftState.editDetails.isEditMode = true;
      });
    }

    case fromActions.DeliveryChecklistActionTypes.EDIT_DIGICHECK_REQUEST_SUCCESS: {
      return produce(state, draftState => {
        const payload = action instanceof fromActions.EditDigiCheckReqSuccess ? action.payload : undefined;
        draftState.editDetails = {
          digiChekId: payload.checklistModel.digichkId,
          isSubmitted: true,
        };
      });
    }

    case fromActions.DeliveryChecklistActionTypes.CANCEL_DIGICHECK: {
      return produce(state, _draftState => {
      });
    }

    case fromActions.DeliveryChecklistActionTypes.CANCEL_DIGICHECK_SUCCESS: {
      return produce(state, draftState => {
        const checklistId = action instanceof fromActions.CancelChecklistSuccess ? action.payload.digitalChecklistId : '';
        draftState.checklistSummary.data = draftState.checklistSummary.data
          .filter(a => a.digichkId !== checklistId);
        draftState.checklistSummary.allChecklistCount = state.checklistSummary.allChecklistCount-1;
      });
    }

    case fromActions.DeliveryChecklistActionTypes.CANCEL_DIGICHECK_FAILURE: {
      return produce(state, draftState => {
        draftState.checklistSummary.error = action instanceof fromActions.CancelChecklistFailure ? action.payload.error : '';
      });
    }

    case fromActions.DeliveryChecklistActionTypes.SEND_EMAIL: {
      return produce(state, _draftState => {
      });
    }

    case fromActions.DeliveryChecklistActionTypes.SEND_EMAIL_SUCCESS: {
      return produce(state, _draftState => {
        const _payloadChkId = action instanceof fromActions.SendEmailSuccess ? action.payload.digitalChecklistId : '';
      
      });
    }

    case fromActions.DeliveryChecklistActionTypes.SEND_EMAIL_FAILURE: {
      return produce(state, draftState => {
        draftState.checklistRequest.error = action instanceof fromActions.SendEmailFailure ? action.payload.error : '';
      });
    }

    default:
      return state;
  }
}

const initialVehicle: IVehicle = {
  year: null,
  model: null,
  trim: null,
  exteriorColor: null,
  vin: null,
  imageUrl: null,
  make: "",
  source: null
};


const initialCustomer: ICustomer = {
  firstName: '',
  lastName: '',
  emailAddress: '',
  phoneNumber: ''
};

export const initialDeliveryConsultant: ISalesAdvisor = {
  salesAdvisorSignId: null,
  firstName: '',
  lastName: '',
  ownerId: ''
}

const initialChecklist: IDigitalChecklistForm = {
  salesConsultant: '',
  dateOfAcquired: '',
  dateOfDeletion:'',
  customer: initialCustomer,
  vehicle: initialVehicle,
  checklistRequest: [],
  deliveryConsultant: initialDeliveryConsultant,
  customerSignId: '',
  scheduledTime: '',
  descriptionOfProductsOrServices: ''
};
export const initialState: DeliveryChecklistState = {
  editDetails: {
    digiChekId: ''
  },
  checklistSummary: {
    data: [],
    loading: false,
    loaded: false,
    error: '',
    allChecklistCount: null,
    totalCount:null
  },
  archivedChecklists: {
    data: [],
    loading: false,
    loaded: false,
    error: '',
    archivedChecklistsCount:null,
    totalCount:null,
    year:null,
    month:null
  },
  digitalChecklist: initialChecklist,
  checklistRequest: {
    data: [],
    loading: false,
    loaded: false,
    error: '',
  }
}